import React, { useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import FormFeedback from '../../../../components/Forms/FormFeedback';
import { objectIsEmpty } from '../../../../util/helpers';

const REQUEST_WIFI_PASSWORD = gql`
  mutation RequestWifiPassword($data: PropertyWifiPasswordRequestInput!) {
    requestPropertyWifiPassword(data: $data) {
      guest {
        id
      }
    }
  }
`;

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    emailAddress: Yup.string()
        .email('Please enter a valid email')
        .required('Email is required'),
    phoneNumber: Yup.string()
        .required('Phone number is required')
        .matches(phoneRegExp, 'Phone number is not valid'),
    propertyId: Yup.string().required('Please select a property'),
});

const WifiPasswordForm = ({ properties = [] }) => {
    const [formStatus, setFormStatus] = useState(null);

    const [requestWifiPassword, { loading: mutationLoading }] = useMutation(
        REQUEST_WIFI_PASSWORD
    );

    return (
        <Formik
            initialValues={{
                firstName: '',
                lastName: '',
                emailAddress: '',
                phoneNumber: '',
                propertyId: '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, setStatus, setErrors, resetForm }) => {
                try {
                    setSubmitting(true);

                    const response = await requestWifiPassword({
                        variables: {
                            data: {
                                firstName: values.firstName,
                                lastName: values.lastName,
                                emailAddress: values.emailAddress,
                                phoneNumber: values.phoneNumber,
                                propertyId: parseInt(values.propertyId),
                            },
                        },
                    });

                    setFormStatus({
                        success: true
                    });
                    resetForm();
                } catch (err) {
                    console.error('Error submitting WiFi access request:', err);
                    setSubmitting(false);
                    setStatus({
                        error: 'There was a problem with your submission. Please check your data and try again.',
                    });
                    setErrors({
                        message: 'There was a problem with your submission. Please check your data and try again.',
                    });
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                submitCount,
                status,
            }) => (
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="firstName">
                            <b>First Name</b>
                            <span className="text-danger ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control rounded"
                            name="firstName"
                            id="firstName"
                            placeholder="Enter first name"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <div className="small text-danger mt-1">
                            {touched.firstName && errors.firstName && errors.firstName}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastName">
                            <b>Last Name</b>
                            <span className="text-danger ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control rounded"
                            name="lastName"
                            id="lastName"
                            placeholder="Enter last name"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <div className="small text-danger mt-1">
                            {touched.lastName && errors.lastName && errors.lastName}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="emailAddress">
                            <b>Email</b>
                            <span className="text-danger ml-1">*</span>
                        </label>
                        <input
                            name="emailAddress"
                            type="email"
                            className="form-control rounded"
                            id="emailAddress"
                            placeholder="Enter email (authorizes contact)"
                            value={values.emailAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <div className="small text-danger mt-1">
                            {touched.emailAddress && errors.emailAddress && errors.emailAddress}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="phoneNumber">
                            <b>Phone</b>
                            <span className="text-danger ml-1">*</span>
                        </label>
                        <input
                            name="phoneNumber"
                            type="text"
                            className="form-control rounded"
                            id="phoneNumber"
                            placeholder="Enter phone (authorizes contact)"
                            value={values.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <div className="small text-danger mt-1">
                            {touched.phoneNumber && errors.phoneNumber && errors.phoneNumber}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="propertyId">
                            <b>Property</b>
                            <span className="text-danger ml-1">*</span>
                        </label>
                        <select
                            name="propertyId"
                            className="form-control rounded custom-select-list"
                            id="propertyId"
                            value={values.propertyId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <option value="" disabled>
                                Select Property
                            </option>
                            {properties.filter(property => property.wifiName && property.type == 'STANDARD').map((property) => (
                                <option key={property.id} value={property.id}>
                                    {property.sitePage?.title}
                                </option>
                            ))}
                        </select>
                        <div className="small text-danger mt-1">
                            {touched.propertyId && errors.propertyId && errors.propertyId}
                        </div>
                    </div>

                    <FormFeedback
                        errors={status?.error}
                        isVisible={
                            status?.error ||
                            formStatus?.success ||
                            (!objectIsEmpty(errors) && submitCount > 0)
                        }
                        alert={!objectIsEmpty(errors) && submitCount > 0}
                    >
                        {formStatus?.success ? (
                            <div className="mt-2">
                                Your Wi-Fi credentials will be sent via email from <strong>al.roberts@beachhousesinparadise.com</strong>.
                                <br />
                                <br />
                                Please add this email address to your contacts to prevent the email from going to your junk/spam folder.
                                <br />
                                <br />
                                <a href="https://beachhousesinparadise.com/whitelist" target="_blank" rel="noopener noreferrer">
                                    How to Whitelist Emails from Beach Houses in Paradise
                                </a>
                            </div>
                        ) : (
                            'Thank you for your request. We will be in touch shortly.'
                        )}
                    </FormFeedback>

                    <button
                        disabled={isSubmitting}
                        type="submit"
                        className="btn btn-secondary text-primary font-weight-bold rounded w-100"
                        style={{ backgroundColor: '#ffd700', border: 'none' }}
                    >
                        {!mutationLoading && !isSubmitting && <span>Send</span>}
                        {(mutationLoading || isSubmitting) && (
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                            />
                        )}
                    </button>
                </form>
            )}
        </Formik>
    );
};

export default WifiPasswordForm;
